import Phaser from 'phaser';
import {
  ACHIEVEMENTS, ACHIEVEMENT_CATEGORIES, getChainTiers,
  SIGIL_SPECIAL_POOL, TOME_DEFINITIONS,
  STORY_QUEST_MAIN_ORDER, STORY_QUEST_SIDE_ORDER,
  isStoryQuestUnlocked,
  lastDailyResetBefore, lastWeeklyResetBefore,
  MASTERY_CHALLENGES,
} from '@roe/shared';
import type { StoryQuest, Quest, MasteryChallenge } from '@roe/shared';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { drawPadlock } from '../utils/glyphs';

// MAX_TRACKED removed — quest tracking widget retired in favor of the
// in-battle score panel. `player.trackedQuests` array stays in saved
// state (read-as-empty in code) so old saves keep deserializing fine,
// but no UI surfaces it any more.

/** Defid → player-facing name for the handful of bosses referenced in story quests.
 *  Falls back to the raw def id if the boss isn't in this map. */
const BOSS_NAME_BY_DEFID: Record<string, string> = {
  boss_lava_golem:    'Magmarath the Undying',
  boss_skeleton_mage: 'The Archivist',
  boss_demon1:        'Abyssal Demon',
  boss_demon2:        'Demon Overlord',
  boss_darkelf:       'Shadow Elf Sovereign',
  boss_voidelf:       'Void Elf Warlord',
  boss_wolf_lord:     'White Claw, Wolf Lord',
  boss_gorgon:        'Gorgon Queen',
  boss_spearman:      'Bone Warlord',
  minotaur_warden:    'Minotaur Warden',
};

function toRoman(n: number): string {
  const vals = [10, 9, 5, 4, 1];
  const syms = ['X', 'IX', 'V', 'IV', 'I'];
  let result = '';
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) { result += syms[i]; n -= vals[i]; }
  }
  return result;
}

export class AchievementScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene: (Phaser.Scene & { resumeBattle(): void }) | null = null;
  private fromPauseMenu = false;
  private contentGroup!: Phaser.GameObjects.Group;
  private scrollOffset = 0;
  private totalContentHeight = 0;
  private readonly VIEWPORT_TOP = 72;
  private readonly VIEWPORT_HEIGHT = 504;
  private activeCategory: string | null = null;
  /** Persisted across opens so the scene reopens on the same tab the user
   *  was last viewing. Default `'daily'` so a fresh session lands on the
   *  rotating Daily / Weekly view (the new primary surface). */
  static lastCategory: string | null = 'daily';
  /** Click-expand popup handles for story-quest beat details. Single instance — clicking
   *  another card closes the previous popup and opens a new one. */
  private storyDetailsObjects: Phaser.GameObjects.GameObject[] | null = null;

  constructor() {
    super({ key: 'AchievementScene' });
  }

  init(data: { gsm: GameStateManager; battleScene?: any; fromPauseMenu?: boolean }): void {
    this.gsm = data.gsm;
    this.battleScene = data.battleScene || null;
    this.fromPauseMenu = data.fromPauseMenu ?? false;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.scrollOffset = 0;
    this.activeCategory = AchievementScene.lastCategory;
    this.cameras.main.setBackgroundColor('rgba(3, 2, 4, 0.98)');
    this.contentGroup = this.add.group();

    const W = 960;

    // ── Full-screen blocking overlay ──
    this.add.rectangle(W / 2, 576 / 2, W, 576, 0x000000, 0).setDepth(0).setInteractive();

    // ── Header bar ──
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    this.add.rectangle(W / 2, 0, W, 72, 0x0a0806, 1).setOrigin(0.5, 0).setDepth(9);

    // Tab-aware header line. Daily / Weekly shows the next-reset countdown
    // (the "log in tomorrow" hook); other tabs keep their natural counter.
    let headerLine = '✦  Quests  ✦';
    if (this.activeCategory === 'daily') {
      const now = Date.now();
      const nextDaily = lastDailyResetBefore(now) + 24 * 60 * 60 * 1000;
      const nextWeekly = lastWeeklyResetBefore(now) + 7 * 24 * 60 * 60 * 1000;
      const fmt = (ms: number): string => {
        const total = Math.max(0, Math.floor(ms / 1000));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };
      headerLine = `✦  Quests  ✦  Daily resets in ${fmt(nextDaily - now)}  ·  Weekly in ${fmt(nextWeekly - now)}`;
    } else if (this.activeCategory === 'story') {
      const storyDone = (this.gsm.player.storyQuests ?? []).filter((q) => q.beats.every((b) => b.done)).length;
      const storyTotal = (this.gsm.player.storyQuests ?? []).length;
      headerLine = `✦  Quests  ✦  Story  ${storyDone} / ${storyTotal} complete`;
    } else if (this.activeCategory === 'heroes') {
      headerLine = '✦  Quests  ✦  Heroes  ·  Repeatable, pay Armory XP';
    } else if (this.activeCategory === 'mastery') {
      const claimedCount = MASTERY_CHALLENGES.filter((c) => this.gsm.isMasteryClaimed(c.id)).length;
      headerLine = `✦  Quests  ✦  Mastery  ${claimedCount} / ${MASTERY_CHALLENGES.length} feats`;
    } else {
      const completed = this.gsm.account.achievements.filter((a) => a.completed).length;
      headerLine = `✦  Quests  ✦  Legacy  ${completed} / ${ACHIEVEMENTS.length} complete`;
    }
    this.add.text(W / 2, 22, headerLine, {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Close button
    const closeBtn = this.add.text(W - 20, 14, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(11).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.closeScene());

    // ESC closes the scene
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.closeScene());
    }

    // ── Claim All button ──
    const unclaimedCount = this.gsm.getUnclaimedAchievementCount();
    const claimAllBg = this.add.rectangle(W - 110, 14, 90, 22, unclaimedCount > 0 ? 0x0e1a0e : 0x0d0a08, 1)
      .setStrokeStyle(1, unclaimedCount > 0 ? 0x3a7a30 : 0x2a1800)
      .setDepth(11).setInteractive({ useHandCursor: unclaimedCount > 0 });
    const claimAllTxt = this.add.text(W - 110, 14,
      unclaimedCount > 0 ? `Claim All (${unclaimedCount})` : 'Claim All',
      { fontSize: '9px', fontFamily: 'Georgia, serif', color: unclaimedCount > 0 ? '#6aaa60' : '#3a2800', fontStyle: 'bold', padding: { x: 4, y: 4 } },
    ).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: unclaimedCount > 0 });
    if (unclaimedCount > 0) {
      claimAllBg.on('pointerover', () => { claimAllTxt.setColor('#c8973a'); claimAllBg.setStrokeStyle(1, 0x9a6020); });
      claimAllBg.on('pointerout', () => { claimAllTxt.setColor('#6aaa60'); claimAllBg.setStrokeStyle(1, 0x3a7a30); });
      const doClaimAll = () => {
        const unclaimed = this.gsm.account.achievements.filter((a) => a.completed && !a.claimed);
        let totalGems = 0;
        for (const a of unclaimed) {
          const def = ACHIEVEMENTS.find((d) => d.id === a.id);
          if (def && this.gsm.claimAchievement(a.id)) totalGems += def.rewards.loot;
        }
        this.gsm.save();
        // Show popup
        const popW = 220; const popH = 60;
        const popBg = this.add.rectangle(W / 2, 576 / 2, popW, popH, 0x0e1a0e, 0.97)
          .setStrokeStyle(2, 0x3a7a30).setDepth(100);
        const popTxt = this.add.text(W / 2, 576 / 2, `Claimed ${unclaimed.length} quest${unclaimed.length !== 1 ? 's' : ''}!\n◆ +${totalGems} Runes`, {
          fontSize: '13px', fontFamily: 'Georgia, serif', color: '#6aaa60', fontStyle: 'bold',
          align: 'center', lineSpacing: 6,
        }).setOrigin(0.5).setDepth(101);
        this.time.delayedCall(2000, () => { popBg.destroy(); popTxt.destroy(); this.scene.restart(); });
      };
      claimAllBg.on('pointerdown', doClaimAll);
      claimAllTxt.on('pointerdown', doClaimAll);
    }

    // ── Tabs — Daily/Weekly default · Mastery · Story · Heroes · Legacy.
    // Mastery tab added in Phase 4 — one-time, account-wide feats (clear
    // a zone fast, no-deaths run, etc.) that pay rune rewards on first
    // clear. activeCategory sentinel values:
    //   'daily'   → Daily / Weekly view (default)
    //   'mastery' → Mastery Challenges (Phase 4)
    //   'story'   → Story chain
    //   'heroes'  → repeatable hero quests
    //   null      → Legacy achievement chains
    const TAB_W = 95;
    const TAB_GAP = 8;
    const tabCount = 5;
    const tabsRowW = TAB_W * tabCount + TAB_GAP * (tabCount - 1);
    const tabStartX = (W - tabsRowW) / 2 + TAB_W / 2;

    const makeTab = (tx: number, label: string, isActive: boolean, color: string, onClick: () => void) => {
      const tabBg = this.add.rectangle(tx, 60, TAB_W - 6, 22, isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? Phaser.Display.Color.HexStringToColor(color).color : 0x5a3600)
        .setDepth(11).setInteractive({ useHandCursor: true });
      const tabTxt = this.add.text(tx, 60, label, {
        fontSize: '11px', fontFamily: 'Georgia, serif',
        color: isActive ? color : '#6b4020',
        padding: { x: 2, y: 4 }, fontStyle: isActive ? 'bold' : 'normal',
      }).setOrigin(0.5).setDepth(12).setInteractive({ useHandCursor: true });
      tabBg.on('pointerdown', onClick);
      tabTxt.on('pointerdown', onClick);
      tabBg.on('pointerover', () => { tabTxt.setColor(color); tabBg.setStrokeStyle(1, Phaser.Display.Color.HexStringToColor(color).color); });
      tabBg.on('pointerout', () => {
        tabTxt.setColor(isActive ? color : '#6b4020');
        tabBg.setStrokeStyle(1, isActive ? Phaser.Display.Color.HexStringToColor(color).color : 0x5a3600);
      });
    };

    let tx = tabStartX;
    makeTab(tx, '✦  Daily / Wk', this.activeCategory === 'daily', '#c8973a',
      () => { AchievementScene.lastCategory = 'daily'; this.scrollOffset = 0; this.scene.restart(); });
    tx += TAB_W + TAB_GAP;
    makeTab(tx, '✦  Mastery', this.activeCategory === 'mastery', '#ffe060',
      () => { AchievementScene.lastCategory = 'mastery'; this.scrollOffset = 0; this.scene.restart(); });
    tx += TAB_W + TAB_GAP;
    makeTab(tx, '✦  Story', this.activeCategory === 'story', '#d4a84a',
      () => { AchievementScene.lastCategory = 'story'; this.scrollOffset = 0; this.scene.restart(); });
    tx += TAB_W + TAB_GAP;
    makeTab(tx, '✦  Heroes', this.activeCategory === 'heroes', '#a78bfa',
      () => { AchievementScene.lastCategory = 'heroes'; this.scrollOffset = 0; this.scene.restart(); });
    tx += TAB_W + TAB_GAP;
    makeTab(tx, '✦  Legacy', this.activeCategory === null, '#7a5a30',
      () => { AchievementScene.lastCategory = null; this.scrollOffset = 0; this.scene.restart(); });

    // Touched but unused references — keep imports valid even though we no
    // longer iterate ACHIEVEMENT_CATEGORIES into per-category tabs.
    void ACHIEVEMENT_CATEGORIES;

    // ── Scroll handling ──
    this.input.on('wheel', (_ptr: unknown, _gos: unknown, _dx: unknown, dy: number) => {
      this.scroll(dy > 0 ? 60 : -60);
    });
    let dragStartY = 0, dragScrollStart = 0, isDragging = false;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < this.VIEWPORT_TOP) return;
      dragStartY = pointer.y; dragScrollStart = this.scrollOffset; isDragging = false;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const dy = dragStartY - pointer.y;
      if (Math.abs(dy) > 4) isDragging = true;
      if (isDragging) {
        this.scrollOffset = Phaser.Math.Clamp(dragScrollStart + dy, 0, Math.max(0, this.totalContentHeight - this.VIEWPORT_HEIGHT));
        this.refreshContent();
      }
    });

    this.gsm.checkAchievements();
    this.refreshContent();
  }

  private scroll(delta: number): void {
    this.scrollOffset = Math.max(0, Math.min(Math.max(0, this.totalContentHeight - this.VIEWPORT_HEIGHT), this.scrollOffset + delta));
    this.refreshContent();
  }

  private refreshContent(): void {
    this.contentGroup.clear(true, true);
    const W = 960;
    // 3-column compact grid — was 2 × 440 wide which spilled to the
    // canvas edges and made cards feel oversized. New layout fits 3
    // cards per row centered with comfortable margins on either side.
    const COLS = 3;
    const COL_W = 290;
    const COL_GAP = 14;
    const TOTAL_W = COLS * COL_W + (COLS - 1) * COL_GAP;
    const LEFT_MARGIN = Math.floor((W - TOTAL_W) / 2);
    const PAD = LEFT_MARGIN; // alias kept for the few legacy renders below
    const colX = (col: number): number => LEFT_MARGIN + col * (COL_W + COL_GAP);
    let y = this.VIEWPORT_TOP + 8 - this.scrollOffset;

    // ── Daily / Weekly tab (the new primary surface) ──────────────────
    if (this.activeCategory === 'daily') {
      const dailies = this.gsm.player.quests.filter((q) => q.type === 'daily');
      const weeklies = this.gsm.player.quests.filter((q) => q.type === 'weekly');
      const renderQuestSection = (label: string, color: string, list: Quest[]): void => {
        if (list.length === 0) return;
        if (y > -30 && y < 576) {
          this.contentGroup.add(this.add.text(W / 2, y, label, {
            fontSize: '11px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
            padding: { x: 4, y: 4 },
          }).setOrigin(0.5));
        }
        y += 22;
        list.forEach((q, i) => {
          const col = i % COLS;
          const x = colX(col);
          if (y > -90 && y < 620) {
            this.renderQuestCard(x, y, COL_W, q);
          }
          if (col === COLS - 1 || i === list.length - 1) y += 92;
        });
        y += 14;
      };
      renderQuestSection('☀  DAILY', '#ffe8a0', dailies);
      renderQuestSection('☾  WEEKLY', '#a8d4ff', weeklies);
      this.totalContentHeight = y + this.scrollOffset - this.VIEWPORT_TOP;
      return;
    }

    // ── Mastery tab — one-time, account-wide feats ────────────────────
    if (this.activeCategory === 'mastery') {
      const infernoUnlocked = !!this.gsm.account.infernoUnlocked;
      // Hide inferno-gated challenges until inferno is actually unlocked,
      // so a fresh account doesn't see entries it can't possibly progress.
      const visibleChallenges = MASTERY_CHALLENGES.filter((c) => !c.requiresInferno || infernoUnlocked);
      const claimed = visibleChallenges.filter((c) => this.gsm.isMasteryClaimed(c.id));
      const unclaimed = visibleChallenges.filter((c) => !this.gsm.isMasteryClaimed(c.id));
      const renderMasterySection = (label: string, color: string, list: MasteryChallenge[]): void => {
        if (list.length === 0) return;
        if (y > -30 && y < 576) {
          this.contentGroup.add(this.add.text(W / 2, y, label, {
            fontSize: '11px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
            padding: { x: 4, y: 4 },
          }).setOrigin(0.5));
        }
        y += 22;
        list.forEach((c, i) => {
          const col = i % COLS;
          const x = colX(col);
          if (y > -90 && y < 620) {
            this.renderMasteryCard(x, y, COL_W, c);
          }
          if (col === COLS - 1 || i === list.length - 1) y += 92;
        });
        y += 14;
      };
      renderMasterySection('☆  ACTIVE FEATS', '#ffe060', unclaimed);
      renderMasterySection('✦  COMPLETED', '#6aaa60', claimed);
      this.totalContentHeight = y + this.scrollOffset - this.VIEWPORT_TOP;
      return;
    }

    // ── Heroes tab — repeatable per-hero quests that pay Armory XP ──
    if (this.activeCategory === 'heroes') {
      // Build the unique hero list — one entry per heroKey on the current
      // save's roster. Bench heroes count too so a player can pick up
      // armory progress without re-deploying.
      const seen = new Set<string>();
      const heroes = this.gsm.player.heroes.filter((h) => {
        const k = h.heroKey ?? h.heroClass;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      if (heroes.length === 0) {
        this.contentGroup.add(this.add.text(W / 2, this.VIEWPORT_TOP + 100,
          'No heroes recruited yet — pick one to start tracking quests.', {
            fontSize: '12px', fontFamily: 'Georgia, serif',
            color: '#7a5a30', fontStyle: 'italic', align: 'center',
          }).setOrigin(0.5));
        this.totalContentHeight = 200;
        return;
      }
      // Render all 3 quest kinds for each hero in one flat 3-col grid.
      const cards: Array<{ hero: typeof heroes[number]; kind: 'kills' | 'damage' | 'stage' }> = [];
      for (const h of heroes) {
        cards.push({ hero: h, kind: 'kills' });
        cards.push({ hero: h, kind: 'damage' });
        cards.push({ hero: h, kind: 'stage' });
      }
      if (y > -30 && y < 576) {
        this.contentGroup.add(this.add.text(W / 2, y, '✦  HERO QUESTS  ·  Bronze → Silver → Gold (5 loops to promote)', {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a78bfa',
          fontStyle: 'bold', padding: { x: 4, y: 4 },
        }).setOrigin(0.5));
      }
      y += 22;
      cards.forEach((c, i) => {
        const col = i % COLS;
        const x = colX(col);
        if (y > -90 && y < 620) {
          this.renderHeroQuestCard(x, y, COL_W, c.hero, c.kind);
        }
        if (col === COLS - 1 || i === cards.length - 1) y += 92;
      });
      this.totalContentHeight = y + this.scrollOffset - this.VIEWPORT_TOP;
      return;
    }

    // ── Story Quests section — only shown when the Story tab is active.
    // Legacy tab renders just achievement chains (no story cards).
    const showStory = this.activeCategory === 'story';
    const storyQuests = this.gsm.player.storyQuests ?? [];
    if (showStory && storyQuests.length > 0) {
      // Build chronological lists: main chain (top), side quests (bottom).
      // Quests not in either order array fall through to a third bucket so
      // any new quest still renders even before its display-order is wired.
      const byId = new Map(storyQuests.map((q) => [q.id, q]));
      const main = STORY_QUEST_MAIN_ORDER.map((id) => byId.get(id)).filter((q): q is StoryQuest => !!q);
      const side = STORY_QUEST_SIDE_ORDER.map((id) => byId.get(id)).filter((q): q is StoryQuest => !!q);
      const orderedIds = new Set<string>([...main.map((q) => q.id), ...side.map((q) => q.id)]);
      const tail = storyQuests.filter((q) => !orderedIds.has(q.id));

      const renderSection = (label: string, color: string, list: StoryQuest[]): void => {
        if (list.length === 0) return;
        if (y > -30 && y < 576) {
          const hdr = this.add.text(W / 2, y, label, {
            fontSize: '11px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
            padding: { x: 4, y: 4 },
          }).setOrigin(0.5);
          this.contentGroup.add(hdr);
        }
        y += 22;
        list.forEach((sq, i) => {
          const col = i % COLS;
          const x = colX(col);
          if (y > -90 && y < 620) {
            this.renderStoryQuestCard(x, y, COL_W, sq);
          }
          if (col === COLS - 1 || i === list.length - 1) y += 92;
        });
        y += 14;
      };

      renderSection('✦  STORY', '#d4a84a', main);
      renderSection('✧  SIDE QUESTS', '#a07840', side);
      if (tail.length > 0) renderSection('· OTHER QUESTS ·', '#7a5a30', tail);
    }
    // When the Story tab is active we skip the achievement chains entirely.
    if (this.activeCategory === 'story') {
      this.totalContentHeight = y + this.scrollOffset - this.VIEWPORT_TOP;
      return;
    }

    const chainsByCategory = new Map<string, string[]>();
    for (const def of ACHIEVEMENTS) {
      if (this.activeCategory && def.category !== this.activeCategory) continue;
      const chains = chainsByCategory.get(def.category) || [];
      if (!chains.includes(def.chain)) chains.push(def.chain);
      chainsByCategory.set(def.category, chains);
    }

    for (const [catKey, chainIds] of chainsByCategory) {
      const catMeta = ACHIEVEMENT_CATEGORIES[catKey];
      if (!catMeta) continue;

      if (y > -30 && y < 576) {
        const hdr = this.add.text(W / 2, y, `${catMeta.icon}  ${catMeta.label.toUpperCase()}`, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: catMeta.color, fontStyle: 'bold',
          padding: { x: 4, y: 4 },
        }).setOrigin(0.5);
        this.contentGroup.add(hdr);
      }
      y += 22;

      chainIds.forEach((chainId, i) => {
        const tiers = getChainTiers(chainId);
        const activeTier = this.getActiveTierForChain(tiers);
        if (!activeTier) return;

        const { def, progress, tierIndex, totalTiers, nextTier } = activeTier;
        const col = i % COLS;
        const x = colX(col);

        if (y > -90 && y < 620) {
          this.renderCard(x, y, COL_W, def, progress, tierIndex, totalTiers, nextTier);
        }

        if (col === COLS - 1 || i === chainIds.length - 1) y += 92;
      });
      y += 12;
    }

    this.totalContentHeight = y + this.scrollOffset - this.VIEWPORT_TOP;
  }

  private getActiveTierForChain(tiers: typeof ACHIEVEMENTS): {
    def: typeof ACHIEVEMENTS[0];
    progress: { current: number; completed: boolean; claimed: boolean };
    tierIndex: number;
    totalTiers: number;
    nextTier: typeof ACHIEVEMENTS[0] | null;
  } | null {
    // Priority 1: show any completed-but-unclaimed tier so the reward is always claimable
    // even if the player advanced to a higher tier without claiming
    for (let i = 0; i < tiers.length; i++) {
      const def = tiers[i];
      const progress = this.gsm.account.achievements.find((a) => a.id === def.id);
      if (progress && progress.completed && !progress.claimed) {
        return { def, progress, tierIndex: i, totalTiers: tiers.length, nextTier: tiers[i + 1] || null };
      }
    }

    // Priority 2: first uncompleted tier
    for (let i = 0; i < tiers.length; i++) {
      const def = tiers[i];
      const progress = this.gsm.account.achievements.find((a) => a.id === def.id);
      if (!progress) continue;
      if (!progress.completed) {
        if (i > 0) {
          const prevProg = this.gsm.account.achievements.find((a) => a.id === tiers[i - 1].id);
          if (!prevProg || !prevProg.completed) {
            return { def, progress: { current: 0, completed: false, claimed: false }, tierIndex: i, totalTiers: tiers.length, nextTier: tiers[i + 1] || null };
          }
        }
        progress.current = this.gsm['getChainStatValue'](def.chain);
        return { def, progress, tierIndex: i, totalTiers: tiers.length, nextTier: tiers[i + 1] || null };
      }
    }

    // Priority 3: all claimed — show last tier
    const last = tiers[tiers.length - 1];
    const lastP = this.gsm.account.achievements.find((a) => a.id === last.id);
    if (lastP) return { def: last, progress: lastP, tierIndex: tiers.length - 1, totalTiers: tiers.length, nextTier: null };
    return null;
  }

  /** Story-quest card — same dimensions as the achievement card so the grid stays uniform.
   *  Shows: name + zone tag + active beat description + progress bar + reward icon on the
   *  right. Click the card to expand a detail popup listing every beat + concrete
   *  requirement. Prereq-gating removed for now — all quests are independent. */
  /** Render a single daily / weekly quest card. Compact 290×88 layout
   *  designed for 3-per-row grid: glyph + name + reward badge on row 1,
   *  description on row 2 (word-wrapped), full-width progress bar / claim
   *  button on row 3. Daily uses gold accents, weekly uses cool blue. */
  private renderQuestCard(x: number, y: number, w: number, q: Quest): void {
    const isDaily = q.type === 'daily';
    const isCompleted = q.status === 'completed';
    const isClaimed = q.status === 'claimed';
    const accent = isDaily ? '#ffe8a0' : '#a8d4ff';
    const accentHex = isDaily ? 0xffe8a0 : 0xa8d4ff;
    const dimAccent = isDaily ? '#a07840' : '#5a7090';
    const baseColor = isClaimed ? '#5a7050' : isCompleted ? accent : dimAccent;
    const bgColor = isClaimed ? 0x0e140e : isCompleted ? 0x141a10 : 0x0d0a08;
    const borderColor = isClaimed ? 0x4a7840 : isCompleted ? accentHex : 0x5a3600;

    const cardH = 88;
    const bg = this.add.rectangle(x + w / 2, y + cardH / 2, w, cardH, bgColor, 0.95)
      .setStrokeStyle(1, borderColor, isClaimed || isCompleted ? 1 : 0.7);
    this.contentGroup.add(bg);

    // Row 1 — glyph (left) + name (mid) + reward badge (right).
    const glyph = isDaily ? '☀' : '☾';
    this.contentGroup.add(this.add.text(x + 14, y + 14, glyph, {
      fontSize: '14px', fontFamily: 'Georgia, serif',
      color: isClaimed ? '#5a7050' : accent,
    }).setOrigin(0.5));
    this.contentGroup.add(this.add.text(x + 26, y + 6, q.name, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: baseColor, fontStyle: 'bold',
    }));
    // Reward badge — top-right corner. "◆ 25" matches the rune glyph used
    // elsewhere; dimmed when claimed since the payoff is already in hand.
    this.contentGroup.add(this.add.text(x + w - 8, y + 8,
      `◆ ${q.rewards.loot}`, {
        fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: isClaimed ? '#4a6040' : '#c8973a', fontStyle: 'bold',
      }).setOrigin(1, 0));

    // Row 2 — description (word-wrap to card width minus padding).
    this.contentGroup.add(this.add.text(x + 26, y + 22, q.description, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: isClaimed ? '#4a6040' : '#6b4020',
      wordWrap: { width: w - 36 },
    }));

    // Row 3 — progress bar (full width) + label below, OR claim button.
    const fmtShort = (n: number): string => {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
      if (n >= 10_000)    return (n / 1_000).toFixed(0) + 'K';
      if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
      return `${n}`;
    };
    const pct = Math.min(q.currentAmount / q.targetAmount, 1);
    const barW = w - 24;
    const barX = x + 12 + barW / 2;
    const barY = y + cardH - 22;

    if (isCompleted && !isClaimed) {
      // Replace the progress bar with a full-width green CLAIM button.
      const btnH = 22;
      const claimBg = this.add.rectangle(x + w / 2, y + cardH - 14, w - 16, btnH, 0x0e1a0e, 1)
        .setStrokeStyle(1, 0x3a7a30).setInteractive({ useHandCursor: true });
      const claimTxt = this.add.text(x + w / 2, y + cardH - 14, `CLAIM  ◆ ${q.rewards.loot}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6aaa60', fontStyle: 'bold',
        padding: { x: 4, y: 4 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      const doClaim = () => {
        if (this.gsm.claimQuest(q.id)) {
          this.gsm.save();
          this.scene.restart();
        }
      };
      claimBg.on('pointerdown', doClaim);
      claimTxt.on('pointerdown', doClaim);
      claimBg.on('pointerover', () => { claimTxt.setColor('#a8e090'); claimBg.setStrokeStyle(1, 0x6aaa60); });
      claimBg.on('pointerout',  () => { claimTxt.setColor('#6aaa60'); claimBg.setStrokeStyle(1, 0x3a7a30); });
      this.contentGroup.add(claimBg);
      this.contentGroup.add(claimTxt);
    } else {
      this.contentGroup.add(this.add.rectangle(barX, barY, barW, 5, 0x2a1a08));
      if (pct > 0) {
        const fillColor = isClaimed ? 0x4a7840 : (isDaily ? 0xc8973a : 0x5a90c0);
        this.contentGroup.add(this.add.rectangle(x + 12 + (barW * pct) / 2, barY, barW * pct, 5, fillColor));
      }
      // Progress label below the bar — "1.2K / 2K".
      this.contentGroup.add(this.add.text(x + 12, y + cardH - 10,
        `${fmtShort(q.currentAmount)} / ${fmtShort(q.targetAmount)}`, {
          fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
          color: isClaimed ? '#4a6040' : '#a07840', fontStyle: 'bold',
        }).setOrigin(0, 0.5));
      if (isClaimed) {
        // Right-aligned claimed marker so the user can tell at a glance
        // why a "completed" card has no claim button.
        this.contentGroup.add(this.add.text(x + w - 12, y + cardH - 10, '✦  Claimed', {
          fontSize: '9px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: '#6aaa60', fontStyle: 'bold',
        }).setOrigin(1, 0.5));
      }
    }
  }

  /** Render a single Mastery Challenge card. Same compact 290×88 shape
   *  as the other quest cards. State: claimed = green border + ✦ Sealed
   *  marker; unclaimed = gold border + condition recap on the bar row.
   *  Mastery has no claim button — the reward fires automatically the
   *  moment the run condition is met (toast in BattleScene). */
  private renderMasteryCard(x: number, y: number, w: number, c: MasteryChallenge): void {
    const claimed = this.gsm.isMasteryClaimed(c.id);
    const accent = claimed ? '#a8e090' : '#ffe060';
    const accentHex = claimed ? 0xa8e090 : 0xffe060;
    const baseColor = claimed ? '#6aaa60' : '#a07840';
    const bgColor = claimed ? 0x0e140e : 0x0d0a08;
    const borderColor = claimed ? 0x4a7840 : 0x5a3600;

    const cardH = 88;
    const bg = this.add.rectangle(x + w / 2, y + cardH / 2, w, cardH, bgColor, 0.95)
      .setStrokeStyle(1, borderColor, claimed ? 1 : 0.7);
    this.contentGroup.add(bg);

    // Row 1 — glyph + name + reward badge
    this.contentGroup.add(this.add.text(x + 14, y + 14, claimed ? '✦' : '☆', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: accent,
    }).setOrigin(0.5));
    this.contentGroup.add(this.add.text(x + 26, y + 6, c.name, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: claimed ? accent : baseColor, fontStyle: 'bold',
    }));
    this.contentGroup.add(this.add.text(x + w - 8, y + 8, `◆ ${c.runes}`, {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: claimed ? '#6aaa60' : '#c8973a', fontStyle: 'bold',
    }).setOrigin(1, 0));

    // Row 2 — description (word-wrapped to fit narrow card)
    this.contentGroup.add(this.add.text(x + 26, y + 22, c.description, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: claimed ? '#5a7050' : '#6b4020',
      wordWrap: { width: w - 36 },
    }));

    // Row 3 — status line. Claimed shows ✦ Sealed; unclaimed shows the
    // current personal-best for fast-zone (so the player knows how close
    // they are) or just "Not yet completed".
    let statusLeft = '';
    let statusColor = '#a07840';
    if (claimed) {
      statusLeft = '✦ Sealed';
      statusColor = '#6aaa60';
    } else if (c.kind === 'fast_zone' && this.gsm.account.masteryBestZoneTime !== undefined) {
      statusLeft = `Best: ${this.gsm.account.masteryBestZoneTime}s`;
    } else if (c.kind === 'high_stage') {
      const peak = this.gsm.account.highestStageEverReached ?? 0;
      statusLeft = `Best: Stage ${peak}`;
    } else if (c.kind === 'inferno_zone') {
      statusLeft = 'Inferno only';
      statusColor = '#cc4030';
    } else {
      statusLeft = 'Single-run condition';
      statusColor = '#7a5a30';
    }
    // Bar — progress visualization for stage / inferno; thin line for the
    // run-condition challenges where progress can't be cleanly inferred.
    const barW = w - 24;
    const barX = x + 12 + barW / 2;
    const barY = y + cardH - 22;
    let pct = 0;
    if (claimed) pct = 1;
    else if (c.kind === 'high_stage') pct = Math.min(1, (this.gsm.account.highestStageEverReached ?? 0) / c.target);
    this.contentGroup.add(this.add.rectangle(barX, barY, barW, 5, 0x2a1a08));
    if (pct > 0) {
      this.contentGroup.add(this.add.rectangle(x + 12 + (barW * pct) / 2, barY, barW * pct, 5, accentHex));
    }
    this.contentGroup.add(this.add.text(x + 12, y + cardH - 10, statusLeft, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: statusColor, fontStyle: 'bold',
    }).setOrigin(0, 0.5));
  }

  /** Render a single hero quest card (kills / damage / stage for one hero).
   *  Same compact 290×88 shape as `renderQuestCard` so the grid stays
   *  consistent. Tier label sits where the type tag would on a daily card.
   *  Reward = Armory XP (not runes), so the badge reads "+30 XP" etc. */
  private renderHeroQuestCard(
    x: number, y: number, w: number,
    hero: import('@roe/shared').Hero,
    kind: 'kills' | 'damage' | 'stage',
  ): void {
    const heroKey = hero.heroKey ?? hero.heroClass;
    const prog = this.gsm.getHeroQuestProgress(heroKey, kind);
    const target = this.gsm.getHeroQuestTarget(heroKey, kind);
    const current = this.gsm.getHeroQuestCurrent(heroKey, kind);
    const reward = this.gsm.getHeroQuestReward(heroKey, kind);
    const isCompleted = current >= target;
    const tier = prog.currentTier;
    const tierColor = tier === 'bronze' ? '#cd7f32' : tier === 'silver' ? '#c0c0c0' : '#ffd700';
    const tierColorHex = tier === 'bronze' ? 0xcd7f32 : tier === 'silver' ? 0xc0c0c0 : 0xffd700;
    const fillColor = tier === 'bronze' ? 0xa0612a : tier === 'silver' ? 0x9aaab0 : 0xc8a040;

    const cardH = 88;
    const bg = this.add.rectangle(x + w / 2, y + cardH / 2, w, cardH,
      isCompleted ? 0x141a10 : 0x0d0a08, 0.95)
      .setStrokeStyle(1, isCompleted ? tierColorHex : 0x5a3600, isCompleted ? 1 : 0.7);
    this.contentGroup.add(bg);

    // Row 1 — hero name + kind label + reward badge
    const kindLabel = kind === 'kills' ? 'Kills' : kind === 'damage' ? 'Damage' : 'Stage';
    const kindGlyph = kind === 'kills' ? '⚔' : kind === 'damage' ? '✦' : '▲';
    this.contentGroup.add(this.add.text(x + 14, y + 14, kindGlyph, {
      fontSize: '14px', fontFamily: 'Georgia, serif',
      color: isCompleted ? tierColor : '#a78bfa',
    }).setOrigin(0.5));
    this.contentGroup.add(this.add.text(x + 26, y + 6, `${hero.name} · ${kindLabel}`, {
      fontSize: '11px', fontFamily: 'Georgia, serif',
      color: isCompleted ? tierColor : '#a78bfa', fontStyle: 'bold',
    }));
    this.contentGroup.add(this.add.text(x + w - 8, y + 8, `+${reward} XP`, {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: tierColor, fontStyle: 'bold',
    }).setOrigin(1, 0));

    // Row 2 — tier + completions toward next promotion
    const promoLabel = tier === 'gold'
      ? `${tier.toUpperCase()} · loops: ${prog.tierCompletions}`
      : `${tier.toUpperCase()} · ${prog.tierCompletions}/${5} → ${tier === 'bronze' ? 'Silver' : 'Gold'}`;
    this.contentGroup.add(this.add.text(x + 26, y + 22, promoLabel, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: tierColor, fontStyle: 'italic',
    }));

    // Row 3 — progress bar OR claim button
    const fmtShort = (n: number): string => {
      if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(n >= 10_000_000_000 ? 0 : 1).replace(/\.0$/, '') + 'B';
      if (n >= 1_000_000)     return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
      if (n >= 10_000)        return (n / 1_000).toFixed(0) + 'K';
      if (n >= 1_000)         return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
      return n.toLocaleString();
    };
    const pct = Math.min(current / Math.max(1, target), 1);
    const barW = w - 24;
    const barX = x + 12 + barW / 2;
    const barY = y + cardH - 22;

    if (isCompleted) {
      const claimBg = this.add.rectangle(x + w / 2, y + cardH - 14, w - 16, 22, 0x0e1a0e, 1)
        .setStrokeStyle(1, 0x3a7a30).setInteractive({ useHandCursor: true });
      const claimTxt = this.add.text(x + w / 2, y + cardH - 14, `CLAIM  +${reward} Armory XP`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a8e090', fontStyle: 'bold',
        padding: { x: 4, y: 4 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      const doClaim = () => {
        if (this.gsm.claimHeroQuest(heroKey, kind) > 0) {
          this.gsm.save();
          this.scene.restart();
        }
      };
      claimBg.on('pointerdown', doClaim);
      claimTxt.on('pointerdown', doClaim);
      claimBg.on('pointerover', () => { claimTxt.setColor('#c8f0a8'); claimBg.setStrokeStyle(1, 0x6aaa60); });
      claimBg.on('pointerout',  () => { claimTxt.setColor('#a8e090'); claimBg.setStrokeStyle(1, 0x3a7a30); });
      this.contentGroup.add(claimBg);
      this.contentGroup.add(claimTxt);
    } else {
      this.contentGroup.add(this.add.rectangle(barX, barY, barW, 5, 0x2a1a08));
      if (pct > 0) {
        this.contentGroup.add(this.add.rectangle(x + 12 + (barW * pct) / 2, barY, barW * pct, 5, fillColor));
      }
      this.contentGroup.add(this.add.text(x + 12, y + cardH - 10,
        `${fmtShort(current)} / ${fmtShort(target)}`, {
          fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
          color: '#a07840', fontStyle: 'bold',
        }).setOrigin(0, 0.5));
    }
  }

  private renderStoryQuestCard(x: number, y: number, w: number, sq: StoryQuest): void {
    const claimed = sq.claimed;
    const activeBeat = sq.beats[sq.currentBeat];
    const isComplete = sq.currentBeat >= sq.beats.length;
    // Greyed locked state — quest's prereq isn't claimed yet. Per user: NO
    // lock icon, no emoji, just dimmed colours so the card reads as "future
    // step" while still showing the player what's coming. Resolve against
    // the live storyQuests list so the chronological chain (shadow_veil →
    // archive_beneath → voidelf_rite → ...) all greys correctly.
    const allQuests = this.gsm.player.storyQuests ?? [];
    const isLocked = !claimed && !isStoryQuestUnlocked(sq, allQuests);

    const bgColor     = isLocked ? 0x070504 : claimed ? 0x0e140e : isComplete ? 0x110e08 : 0x0d0a08;
    const borderColor = isLocked ? 0x2a1f10 : claimed ? 0x4a7840 : isComplete ? 0xd4a84a : 0x7a5a30;
    const bg = this.add.rectangle(x + w / 2, y + 38, w, 78, bgColor, isLocked ? 0.55 : 0.95)
      .setStrokeStyle(1, borderColor, isLocked ? 0.4 : (claimed || isComplete ? 1 : 0.7));
    this.contentGroup.add(bg);

    // Click-to-expand — popup lists every beat with the concrete trigger requirement so
    // the quest isn't a mystery ("what do I need to kill? what zone? how many?"). A single
    // popup instance at a time; clicking another card re-opens for that one.
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerdown', () => this.showStoryQuestDetails(sq));

    // Bronze wax-seal stamp — replaces the previous green "CLAIMED"
    // banner with something that reads as a stamped seal in the
    // bronze/runic palette of the rest of the UI. Outer bronze disc,
    // inner pressed-ring detail, runic glyph (✦) at the center.
    // Slight tilt so it reads as a hand-stamped impression.
    if (claimed) {
      const sealX = x + w / 2 - 40;
      const sealY = y + 38;
      const sealAngle = -7;
      const sealDisc = this.add.circle(sealX, sealY, 17, 0x4a2810, 0.95)
        .setStrokeStyle(2, 0xa0612a, 0.95)
        .setDepth(2).setAngle(sealAngle);
      this.contentGroup.add(sealDisc);
      // Inner pressed-ring detail — gives the stamp depth.
      const sealInner = this.add.circle(sealX, sealY, 12, 0, 0)
        .setStrokeStyle(1, 0xc8973a, 0.55)
        .setDepth(2.5).setAngle(sealAngle);
      this.contentGroup.add(sealInner);
      const sealGlyph = this.add.text(sealX, sealY, '✦', {
        fontSize: '15px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#f0c878', fontStyle: 'bold',
        stroke: '#1a0a04', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(3).setAngle(sealAngle);
      this.contentGroup.add(sealGlyph);
    }

    // Icon
    const icon = this.add.text(x + 20, y + 20, '✦', {
      fontSize: '18px', fontFamily: 'Georgia, serif',
      color: isLocked ? '#3a2810' : '#d4a84a',
      padding: { x: 4, y: 4 },
    }).setOrigin(0.5);
    this.contentGroup.add(icon);

    // Name + zone tag
    const nameColor = isLocked ? '#5a4020' : claimed ? '#6aaa60' : '#d4a84a';
    const name = this.add.text(x + 36, y + 6, sq.name, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: nameColor, fontStyle: 'bold',
    });
    this.contentGroup.add(name);
    const zoneTag = this.add.text(x + 36 + name.width + 6, y + 8, sq.zone, {
      fontSize: '8px', fontFamily: 'Georgia, serif',
      color: isLocked ? '#3a2810' : '#7a5a30', fontStyle: 'italic',
    });
    this.contentGroup.add(zoneTag);

    // Active beat / completion line. Locked quests show the active beat's
    // description so the player can see what's coming after they unlock
    // the prereq, but rendered in a dimmer tone so it doesn't read as
    // "actionable now". No "🔒 Requires: …" text — per user, no emoji.
    const detail = isLocked
      ? `Beat 1/${sq.beats.length}: ${sq.beats[0]?.description ?? ''}`
      : claimed
        ? 'Complete — reward claimed.'
        : isComplete
          ? 'All beats complete — claim your reward.'
          : `Beat ${sq.currentBeat + 1}/${sq.beats.length}: ${activeBeat?.description ?? ''}`;
    const desc = this.add.text(x + 36, y + 20, detail, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: isLocked ? '#5a4020' : claimed ? '#5a7050' : isComplete ? '#ffd060' : '#a07840',
      wordWrap: { width: w - 130 }, maxLines: 2,
    });
    this.contentGroup.add(desc);

    // CLAIM button — only on quests that finished every beat but the
    // player hasn't claimed yet. Per user "story quests should be
    // claimable; without claiming, can't access the new feature/book".
    // Until this fires, `quest.claimed` stays false and the gated
    // unlock (relic slot / sigil / book / inferno) is inactive.
    if (isComplete && !claimed && !isLocked) {
      const btnX = x + w - 60;
      const btnY = y + 18;
      const btnBg = this.add.rectangle(btnX, btnY, 84, 22, 0x1a2a10, 0.95)
        .setStrokeStyle(2, 0xa8e090, 0.95).setDepth(4)
        .setInteractive({ useHandCursor: true });
      const btnTxt = this.add.text(btnX, btnY, 'CLAIM', {
        fontSize: '11px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#a8e090', fontStyle: 'bold',
        stroke: '#06100a', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(5);
      this.contentGroup.add(btnBg);
      this.contentGroup.add(btnTxt);
      // Slow pulse so the claim button announces itself.
      this.tweens.add({
        targets: [btnBg, btnTxt], alpha: { from: 1, to: 0.7 },
        duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
      btnBg.on('pointerover', () => { btnBg.setFillStyle(0x2a4020, 1); btnTxt.setColor('#d8ffc8'); });
      btnBg.on('pointerout', () => { btnBg.setFillStyle(0x1a2a10, 0.95); btnTxt.setColor('#a8e090'); });
      btnBg.on('pointerdown', () => {
        const ok = this.gsm.claimStoryQuest(sq.id);
        if (!ok) return;
        // Claim VFX — gold burst expanding ring + bronze inner pulse on
        // the card centre. Phaser-drawn so no asset dependency.
        this.playClaimVfx(x + w / 2, y + 38);
        // Rebuild the scene one tick later so the card flips to the
        // claimed state with its CLAIMED stamp + green border.
        this.time.delayedCall(620, () => this.scene.restart());
      });
    }

    // Progress bar (kill-count beats only — zone-enter beats hide the bar).
    if (activeBeat && activeBeat.trigger.kind !== 'enter_zone') {
      const goal = 'count' in activeBeat.trigger ? activeBeat.trigger.count : 1;
      const cur  = Math.min(goal, Math.floor(activeBeat.progress));
      const pct  = Math.min(1, cur / goal);
      // Bar width shortened from `w - 130` → `w - 180` per user
      // ("reduce progression line so it doesn't touch the icons"). The
      // reward icon at iconX = x + w - 42 has its left edge at x + w - 64;
      // a 50 px shrink leaves a clean ~50 px gap between the progress
      // label "X/Y" and the reward badge.
      const barW = w - 180;
      const barX = x + 36;
      const barY = y + 50;
      this.contentGroup.add(this.add.rectangle(barX + barW / 2, barY, barW, 5, 0x2a1a08));
      if (pct > 0) {
        const fillColor = claimed ? 0x4a7840 : 0xd4a84a;
        this.contentGroup.add(this.add.rectangle(barX + (barW * pct) / 2, barY, barW * pct, 5, fillColor));
      }
      this.contentGroup.add(this.add.text(barX + barW + 6, barY, `${cur}/${goal}`, {
        fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace", color: '#6b4020', fontStyle: 'bold',
      }).setOrigin(0, 0.5));
    }

    // Beat list as small dots — gives a visual sense of total chain length
    const totalBeats = sq.beats.length;
    for (let t = 0; t < totalBeats; t++) {
      const dotColor = sq.beats[t].done ? 0x4a7840 : t === sq.currentBeat && !claimed ? 0xd4a84a : 0x3a2800;
      this.contentGroup.add(this.add.circle(x + 36 + t * 10, y + 64, 3, dotColor));
    }

    // Reward icon — right-anchored, grey + lock until claimed.
    const sigil = SIGIL_SPECIAL_POOL.find((sp) => sp.requiresQuestId === sq.id);
    const tomeId = (Object.keys(TOME_DEFINITIONS) as Array<keyof typeof TOME_DEFINITIONS>)
      .find((id) => TOME_DEFINITIONS[id].requiresQuestId === sq.id);
    // Relic slot unlocks don't live in any pool — hardcoded here since there's only two
    // gated slots. Keeps the badge rendering logic uniform across reward kinds.
    const relicSlot = sq.id === 'shadow_veil' ? 3 : sq.id === 'voidelf_rite' ? 4 : null;
    const reward = sigil
      ? { iconKey: sigil.iconKey, name: sigil.name, kind: 'Sigil' as const }
      : tomeId
        ? { iconKey: '', name: TOME_DEFINITIONS[tomeId].name, kind: 'Tome' as const, glyph: TOME_DEFINITIONS[tomeId].icon, color: TOME_DEFINITIONS[tomeId].color }
        : relicSlot !== null
          ? { iconKey: '', name: `Relic Slot ${relicSlot}`, kind: 'RelicSlot' as const, slot: relicSlot }
          : null;
    if (reward) {
      // Shifted iconX leftward (was x + w - 32) so the reward-name label underneath stays
      // inside the card. Card right edge is at x + w; centering a ~72 px wordwrap at the
      // old iconX pushed the label ~13 px past the edge ("Magmarath's Ember" clipped).
      const iconX = x + w - 42;
      const iconY = y + 38;
      // Claimed-reward celebration — a soft gold glow pulse behind the badge + a slow
      // rotating sparkle ring. Fires indefinitely while the card is visible; draws the
      // eye to the unlock so the player actually notices what's now available.
      if (claimed) {
        const glow = this.add.rectangle(iconX, iconY, 52, 52, 0xc8973a, 0.18);
        this.contentGroup.add(glow);
        this.tweens.add({
          targets: glow, alpha: { from: 0.18, to: 0.42 },
          scale: { from: 1, to: 1.08 },
          duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
        // Four-pointed sparkle glyph that slow-rotates on top of the badge.
        const sparkle = this.add.text(iconX + 14, iconY - 14, '✦', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#ffe8a0',
        }).setOrigin(0.5).setAlpha(0.85);
        this.contentGroup.add(sparkle);
        this.tweens.add({
          targets: sparkle, alpha: { from: 0.5, to: 1 }, scale: { from: 0.85, to: 1.15 },
          duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
      }
      const rewardBg = this.add.rectangle(iconX, iconY, 44, 44, 0x0a0608, 1)
        .setStrokeStyle(1, claimed ? 0x9a6020 : 0x3a2800);
      this.contentGroup.add(rewardBg);
      if (reward.kind === 'Sigil' && this.textures.exists(reward.iconKey)) {
        const img = this.add.image(iconX, iconY, reward.iconKey).setDisplaySize(36, 36);
        if (!claimed) img.setTint(0x404040);
        this.contentGroup.add(img);
      } else if (reward.kind === 'Tome') {
        const glyph = this.add.text(iconX, iconY, reward.glyph, {
          fontSize: '24px', fontFamily: 'Georgia, serif',
          color: claimed ? reward.color : '#404040', fontStyle: 'bold',
        }).setOrigin(0.5);
        this.contentGroup.add(glyph);
      } else if (reward.kind === 'RelicSlot') {
        // Relic-slot reward icon — a Phaser-drawn gem socket with the slot number inside.
        // Octagonal outline + inner ring reads as "equipment slot"; bronze for claimed,
        // dim grey for locked. Bigger, more graphic than the old "Rl N SLOT" text pair.
        const relicGfx = this.add.graphics();
        const color = claimed ? 0xc8973a : 0x404040;
        const accent = claimed ? 0xffe8a0 : 0x606060;
        relicGfx.lineStyle(2, color, 1);
        // Octagon frame (7 px radius octagon, then expanded to 16 px)
        const R = 16;
        const points: number[][] = [];
        for (let i = 0; i < 8; i++) {
          const a = (Math.PI / 4) * i - Math.PI / 8;
          points.push([iconX + Math.cos(a) * R, iconY + Math.sin(a) * R]);
        }
        relicGfx.beginPath();
        relicGfx.moveTo(points[0][0], points[0][1]);
        for (let i = 1; i < 8; i++) relicGfx.lineTo(points[i][0], points[i][1]);
        relicGfx.closePath();
        relicGfx.strokePath();
        // Inner ring accent
        relicGfx.lineStyle(1, accent, 0.6);
        relicGfx.strokeCircle(iconX, iconY, R - 5);
        this.contentGroup.add(relicGfx);
        // Slot number inside the socket
        const slotNum = this.add.text(iconX, iconY, `${reward.slot}`, {
          fontSize: '16px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: claimed ? '#ffe8a0' : '#606060', fontStyle: 'bold',
        }).setOrigin(0.5);
        this.contentGroup.add(slotNum);
      }
      if (!claimed) {
        // Drawn padlock (bronze, no emoji) — same style as the consumable-slot lock.
        const lockGfx = this.add.graphics();
        drawPadlock(lockGfx, iconX + 14, iconY - 16, 0xc85020);
        this.contentGroup.add(lockGfx);
      }
      // Reward-name label — centered DIRECTLY under the icon per user
      // image feedback ("text under icon centered under the icon"). Was
      // right-aligned to x+w-10 which left "Book of Haste" / similar
      // labels sitting visibly off-center. Now origin (0.5, 0) at iconX
      // with center alignment + 92 px wordWrap so long names wrap into
      // two centered lines instead of overflowing one side.
      const labelColor = claimed ? '#6aaa60' : '#6b4020';
      this.contentGroup.add(this.add.text(iconX, iconY + 26, reward.name, {
        fontSize: '8px', fontFamily: 'Georgia, serif', color: labelColor,
        align: 'center', wordWrap: { width: 92 }, lineSpacing: 1,
      }).setOrigin(0.5, 0));
    }
  }

  /** Claim VFX — gold expanding ring + bronze inner pulse + warm spark
   *  particles centered on the claimed card. Mirrors the altar-blessing
   *  burst shape (VFXManager.playAltarBlessing) but lives directly on
   *  this scene so the achievement screen doesn't have to share a VFX
   *  manager with BattleScene. */
  private playClaimVfx(x: number, y: number): void {
    // Outer gold ring — wide, slow fade.
    const outer = this.add.graphics().setDepth(80);
    outer.lineStyle(3, 0xffd060, 0.95);
    outer.strokeCircle(0, 0, 90);
    outer.setPosition(x, y).setScale(0.05).setAlpha(0.95);
    this.tweens.add({
      targets: outer, scaleX: 1, scaleY: 1, alpha: 0,
      duration: 620, ease: 'Cubic.easeOut',
      onComplete: () => outer.destroy(),
    });
    // Inner bronze pulse 120 ms later for a layered double-tap feel.
    this.time.delayedCall(120, () => {
      const inner = this.add.graphics().setDepth(80);
      inner.lineStyle(2, 0xc8973a, 0.85);
      inner.strokeCircle(0, 0, 60);
      inner.setPosition(x, y).setScale(0.05).setAlpha(0.85);
      this.tweens.add({
        targets: inner, scaleX: 1, scaleY: 1, alpha: 0,
        duration: 480, ease: 'Cubic.easeOut',
        onComplete: () => inner.destroy(),
      });
    });
    // Particle burst — gold motes flying outward radially.
    if (this.textures.exists('projectile')) {
      const emitter = this.add.particles(x, y, 'projectile', {
        speed: { min: 90, max: 200 }, angle: { min: 0, max: 360 },
        scale: { start: 0.8, end: 0 }, alpha: { start: 0.95, end: 0 },
        lifespan: 580, tint: [0xffd060, 0xffe8a0, 0xc8973a],
        emitting: false,
      }).setDepth(80);
      emitter.explode(22);
      this.time.delayedCall(800, () => emitter.destroy());
    }
  }

  /** Popup showing every beat of a story quest with the concrete trigger requirement.
   *  Replaces the hover-tooltip approach — click a quest card, get a modal overlay with
   *  the full objective list + live progress. Tap anywhere outside (or the × button) to
   *  close. Only one instance at a time. */
  private showStoryQuestDetails(sq: StoryQuest): void {
    // Close any existing popup first
    this.storyDetailsObjects?.forEach((o) => o.destroy());
    this.storyDetailsObjects = [];
    const objs: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.7)
      .setDepth(200).setInteractive({ useHandCursor: false });
    objs.push(overlay);

    const PW = 360;
    const PH = Math.min(460, 100 + sq.beats.length * 44);
    const PCX = GAME_W / 2;
    const PCY = GAME_H / 2;
    const panel = this.add.rectangle(PCX, PCY, PW, PH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0xc8973a, 0.85).setDepth(201);
    const innerBorder = this.add.rectangle(PCX, PCY, PW - 8, PH - 8, 0, 0)
      .setStrokeStyle(1, 0xc8973a, 0.25).setDepth(201);
    objs.push(panel, innerBorder);

    const titleY = PCY - PH / 2 + 20;
    const title = this.add.text(PCX, titleY, sq.name, {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#d4a84a', fontStyle: 'bold',
      wordWrap: { width: PW - 40 }, align: 'center',
    }).setOrigin(0.5).setDepth(202);
    objs.push(title);
    const subtitle = this.add.text(PCX, titleY + 20, `${sq.zone}${sq.minStage > 1 ? ` · stage ${sq.minStage}+` : ''}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#7a5a30', fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(202);
    objs.push(subtitle);

    // Divider
    const divider = this.add.rectangle(PCX, titleY + 36, PW - 40, 1, 0x5a3600, 0.8).setDepth(202);
    objs.push(divider);

    // Format a beat's trigger into human-readable requirement text.
    const formatReq = (b: typeof sq.beats[0]): string => {
      const t = b.trigger;
      if (t.kind === 'enter_zone') return `Enter ${t.zoneName}`;
      if (t.kind === 'kill_enemy') {
        const who = t.faction ? `${t.faction}` : t.defId ?? 'enemies';
        return `Kill ${t.count} ${who}`;
      }
      if (t.kind === 'reach_stage') return `Reach stage ${t.stage}`;
      if (t.kind === 'reach_stage_no_stay') return `Reach stage ${t.stage} without using Stay`;
      // kill_boss — defId optional so the wildcard "any boss" reads cleanly.
      if (!t.defId) return `Defeat ${t.count} zone boss${t.count === 1 ? '' : 'es'}`;
      const bossName = BOSS_NAME_BY_DEFID[t.defId] ?? t.defId;
      return `Defeat ${bossName}`;
    };

    // Beat rows
    const startY = titleY + 54;
    for (let bi = 0; bi < sq.beats.length; bi++) {
      const b = sq.beats[bi];
      const rowY = startY + bi * 44;
      const isActive = bi === sq.currentBeat && !sq.claimed;
      const glyph = b.done ? '✓' : isActive ? '►' : '○';
      const glyphColor = b.done ? '#6aaa60' : isActive ? '#d4a84a' : '#5a3600';
      const nameColor = b.done ? '#6aaa60' : isActive ? '#d4a84a' : '#6b4020';

      const glyphText = this.add.text(PCX - PW / 2 + 18, rowY, glyph, {
        fontSize: '14px', fontFamily: 'Georgia, serif', color: glyphColor, fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(202);
      objs.push(glyphText);

      const beatName = this.add.text(PCX - PW / 2 + 32, rowY - 8, `${bi + 1}. ${b.name}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: nameColor, fontStyle: 'bold',
        wordWrap: { width: PW - 56 },
      }).setDepth(202);
      objs.push(beatName);

      const progressStr = b.trigger.kind !== 'enter_zone' && 'count' in b.trigger
        ? ` (${Math.min(b.trigger.count, Math.floor(b.progress))}/${b.trigger.count})`
        : '';
      const reqText = this.add.text(PCX - PW / 2 + 32, rowY + 6, formatReq(b) + progressStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
        wordWrap: { width: PW - 56 },
      }).setDepth(202);
      objs.push(reqText);
    }

    // Close button (top-right X) + overlay click both dismiss
    const closeX = this.add.text(PCX + PW / 2 - 16, titleY, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    closeX.on('pointerover', () => closeX.setColor('#c8973a'));
    closeX.on('pointerout', () => closeX.setColor('#5a3600'));
    objs.push(closeX);

    const close = () => {
      objs.forEach((o) => o.destroy());
      this.storyDetailsObjects = null;
    };
    overlay.on('pointerdown', close);
    closeX.on('pointerdown', close);

    this.storyDetailsObjects = objs;
  }

  /** Legacy chain achievement card — narrowed to the new 3-column layout
   *  (~290 wide). Same shape as `renderQuestCard`: glyph + name + reward
   *  badge on row 1, description on row 2 (word-wrapped), progress bar /
   *  claim button on row 3. Preserves the tier "(I/V)" badge and the
   *  green/purple/bronze state palette so claimable chains still pop. */
  private renderCard(
    x: number, y: number, w: number,
    def: typeof ACHIEVEMENTS[0],
    progress: { current: number; completed: boolean; claimed: boolean },
    tierIndex: number, totalTiers: number,
    nextTier: typeof ACHIEVEMENTS[0] | null,
  ): void {
    void nextTier; // next-tier preview line removed — too long for the narrow card.
    const isCompleted = progress.completed;
    const isClaimed = progress.claimed;

    const bgColor = isClaimed ? 0x0e140e : isCompleted ? 0x101208 : 0x0d0a08;
    const borderColor = isClaimed ? 0x4a7840 : isCompleted ? 0x8040a0 : 0x5a3600;
    const cardH = 88;
    const bg = this.add.rectangle(x + w / 2, y + cardH / 2, w, cardH, bgColor, 0.95)
      .setStrokeStyle(1, borderColor, isClaimed || isCompleted ? 1 : 0.7);
    this.contentGroup.add(bg);

    // Row 1 — icon + name (with tier suffix) + reward badge
    this.contentGroup.add(this.add.text(x + 14, y + 14, def.icon, {
      fontSize: '14px', padding: { x: 4, y: 4 },
    }).setOrigin(0.5));
    const nameColor = isClaimed ? '#6aaa60' : isCompleted ? '#b070d0' : '#a07840';
    const tierLabel = totalTiers > 1 ? `  ${toRoman(tierIndex + 1)}/${toRoman(totalTiers)}` : '';
    this.contentGroup.add(this.add.text(x + 26, y + 6, def.name + tierLabel, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: nameColor, fontStyle: 'bold',
    }));
    this.contentGroup.add(this.add.text(x + w - 8, y + 8, `◆ ${def.rewards.loot}`, {
      fontSize: '10px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: isClaimed ? '#4a6040' : '#c8973a', fontStyle: 'bold',
    }).setOrigin(1, 0));

    // Row 2 — description (word-wrap)
    this.contentGroup.add(this.add.text(x + 26, y + 22, def.description, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: isClaimed ? '#5a7050' : '#6b4020',
      wordWrap: { width: w - 36 },
    }));

    // Row 3 — progress bar / claim button
    const fmtShort = (n: number): string => {
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
      if (n >= 10_000)    return (n / 1_000).toFixed(0) + 'K';
      if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
      return n.toLocaleString();
    };
    const pct = Math.min(progress.current / def.target, 1);
    const barW = w - 24;
    const barX = x + 12 + barW / 2;
    const barY = y + cardH - 22;

    if (isCompleted && !isClaimed) {
      // Claim button replaces the bar.
      const claimBg = this.add.rectangle(x + w / 2, y + cardH - 14, w - 16, 22, 0x0e1a0e, 1)
        .setStrokeStyle(1, 0x3a7a30).setInteractive({ useHandCursor: true });
      const claimTxt = this.add.text(x + w / 2, y + cardH - 14, `CLAIM  ◆ ${def.rewards.loot}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6aaa60', fontStyle: 'bold',
        padding: { x: 4, y: 4 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      claimBg.on('pointerover', () => { claimTxt.setColor('#a8e090'); claimBg.setStrokeStyle(1, 0x6aaa60); });
      claimBg.on('pointerout', () => { claimTxt.setColor('#6aaa60'); claimBg.setStrokeStyle(1, 0x3a7a30); });
      const doClaim = () => { this.gsm.claimAchievement(def.id); this.gsm.save(); this.refreshContent(); };
      claimBg.on('pointerdown', doClaim);
      claimTxt.on('pointerdown', doClaim);
      this.contentGroup.add(claimBg);
      this.contentGroup.add(claimTxt);
    } else {
      this.contentGroup.add(this.add.rectangle(barX, barY, barW, 5, 0x2a1a08));
      if (pct > 0) {
        const fillColor = isClaimed ? 0x4a7840 : 0xc8973a;
        this.contentGroup.add(this.add.rectangle(x + 12 + (barW * pct) / 2, barY, barW * pct, 5, fillColor));
      }
      this.contentGroup.add(this.add.text(x + 12, y + cardH - 10,
        `${fmtShort(progress.current)} / ${fmtShort(def.target)}`, {
          fontSize: '9px', fontFamily: "'Consolas', 'Courier New', monospace",
          color: isClaimed ? '#4a6040' : '#a07840', fontStyle: 'bold',
        }).setOrigin(0, 0.5));
      if (isClaimed) {
        const doneLabel = tierIndex < totalTiers - 1 ? '✓ Tier done' : '✦ MAX';
        this.contentGroup.add(this.add.text(x + w - 12, y + cardH - 10, doneLabel, {
          fontSize: '9px', fontFamily: "'Cinzel Decorative', Georgia, serif",
          color: tierIndex < totalTiers - 1 ? '#6aaa60' : '#c8973a', fontStyle: 'bold',
        }).setOrigin(1, 0.5));
      }
    }
  }

  private closeScene(): void {
    this.scene.stop();
    if (this.fromPauseMenu) {
      this.scene.launch('MenuScene', { gsm: this.gsm, battleScene: this.battleScene });
    } else if (this.battleScene) {
      (this.battleScene as any).resumeBattle();
    } else {
      this.scene.start('MenuScene');
    }
  }
}
